
import React from 'react';
import { CycleData, phaseColors } from '@/utils/cycle-tracking';
import { CircleSegment } from './CircleSegment';

interface CycleCircleProps {
  cycleData: CycleData;
  highlightedDay: number | null;
  setHighlightedDay: (day: number | null) => void;
}

export const CycleCircle: React.FC<CycleCircleProps> = ({
  cycleData,
  highlightedDay,
  setHighlightedDay
}) => {
  const { currentDay, cycleLength, days } = cycleData;
  const segmentDegrees = 360 / cycleLength;
  
  // Generate the segments for the circular view
  const generateCircleSegments = () => {
    const segments = [];
    
    for (let i = 1; i <= cycleLength; i++) {
      // Find the phase for this day
      const dayInfo = days.find(d => d.day === i);
      if (!dayInfo) continue;
      
      const phase = dayInfo.phase;
      const color = phaseColors[phase];
      
      segments.push(
        <CircleSegment
          key={`segment-${i}`}
          day={i}
          currentDay={currentDay}
          phase={phase}
          color={color}
          segmentDegrees={segmentDegrees}
          highlightedDay={highlightedDay}
          onHighlight={setHighlightedDay}
        />
      );
    }
    
    return segments;
  };

  // Phase labels with better positioning and design
  const phaseLabels = [
    { phase: 'menstruation', label: 'Menstruation', position: { top: '15%', right: '30%' }, color: phaseColors.menstruation },
    { phase: 'follicular', label: 'Follicular', position: { top: '30%', left: '15%' }, color: phaseColors.follicular },
    { phase: 'ovulation', label: 'Ovulation', position: { bottom: '30%', left: '20%' }, color: phaseColors.ovulation },
    { phase: 'luteal', label: 'Luteal', position: { bottom: '30%', right: '20%' }, color: phaseColors.luteal }
  ];
  
  return (
    <div className="relative w-full h-full mx-auto">
      {/* Background with subtle gradient */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-hormony-beige/20 to-hormony-cream/30"></div>
      
      {/* Phase background sections with improved color alignment */}
      <div className="absolute inset-0 rounded-full overflow-hidden">
        <div className="absolute inset-0 rounded-full" style={{ background: phaseColors.menstruation, opacity: 0.15, clipPath: 'polygon(50% 50%, 100% 0%, 0% 0%, 50% 50%)' }}></div>
        <div className="absolute inset-0 rounded-full" style={{ background: phaseColors.follicular, opacity: 0.15, clipPath: 'polygon(50% 50%, 0% 0%, 0% 100%, 50% 50%)' }}></div>
        <div className="absolute inset-0 rounded-full" style={{ background: phaseColors.ovulation, opacity: 0.15, clipPath: 'polygon(50% 50%, 0% 100%, 100% 100%, 50% 50%)' }}></div>
        <div className="absolute inset-0 rounded-full" style={{ background: phaseColors.luteal, opacity: 0.15, clipPath: 'polygon(50% 50%, 100% 100%, 100% 0%, 50% 50%)' }}></div>
      </div>
      
      {/* Circle outline */}
      <div className="absolute inset-2 rounded-full border border-hormony-beige/40"></div>
      
      {/* Phase labels with better positioning */}
      {phaseLabels.map((phase) => (
        <div
          key={phase.phase}
          className="absolute text-xs font-medium text-hormony-charcoal"
          style={{ ...phase.position }}
        >
          <div className="flex items-center">
            <div className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: phase.color }}></div>
            {phase.label}
          </div>
        </div>
      ))}
      
      {/* White center of circle */}
      <div className="absolute inset-[70px] rounded-full bg-white shadow-sm z-10"></div>
      
      {/* Cycle day segments */}
      <div className="absolute inset-0 rounded-full flex items-center justify-center">
        {generateCircleSegments()}
      </div>
    </div>
  );
};
